import { ArrowLeft, ChevronRight, Download, X } from "lucide-react";
import { useState } from "react";
import { useLocation } from "wouter";
import { isNative } from "../api";
import { moveNow, useMove } from "../moved";
import { checkForUpdates, installAppUpdate, restartToUpdate, useUpdates } from "../updater";
import { dateFmt } from "./format";
import { usePrefs } from "./prefs";
import RELEASES from "../releases.json";

/* Updates in Settings, and the banner when one waits (see ../updater.ts): the web app and the Android app. */

const mb = (bytes: number) => `${(bytes / 1048576).toFixed(1)} MB`;
const checkedAt = dateFmt({ hour: "numeric", minute: "2-digit" });

/** Settings > Updates: version, update status, "Restart now", and on Android the app update. */
export function AppUpdateSettings() {
  const u = useUpdates();
  const ask = usePrefs((s) => s.askBeforeUpdating);
  const [restarting, setRestarting] = useState(false);
  const version = !isNative ? __APP_BUILD__ : u.info ? `App ${u.info.versionName} · screens ${__APP_BUILD__}` : `Screens ${__APP_BUILD__}`;
  const whenReady = isNative
    ? "Engram switches to it when you come back after a while, once your changes have synced."
    : "Engram switches to it when you're on another tab or window, once your changes have synced.";
  let status: string;
  if (u.checking && u.bundle !== "downloading") status = "Checking…";
  else if (u.bundle === "downloading") status = u.bundleBuild ? `Downloading the new version (${u.bundleBuild})…` : "Downloading the new version…";
  else if (u.bundle === "ready") {
    const which = u.bundleBuild ? ` (${u.bundleBuild})` : "";
    status = ask ? `New version ready${which}. Restart to use it.` : `New version ready${which}. ${whenReady}`;
  }
  else if (u.bundle === "needs-app") status = `A new version (${u.bundleBuild}) needs the app update first.`;
  else if (u.checked) status = `Up to date. Checked at ${checkedAt.format(u.checked)}.`;
  else status = "Not checked yet.";

  return (
    <>
      <div className="settings-row">
        <div>
          <div className="settings-label">Version</div>
          <div className="settings-hint">{version}</div>
          <div className="settings-hint" aria-live="polite">
            {status}
          </div>
          {u.info?.rolledBack && <div className="settings-hint">Version {u.info.rolledBack} didn't start properly, so Engram went back to the one before.</div>}
          {u.error && <div className="settings-hint error-text">{u.error}</div>}
        </div>
        {u.bundle === "ready" ? (
          <button
            className="btn primary small"
            disabled={restarting}
            onClick={async () => {
              setRestarting(true);
              await restartToUpdate();
              setRestarting(false);
            }}
          >
            {restarting ? "Restarting…" : "Restart now"}
          </button>
        ) : (
          <button className="btn small" disabled={u.checking} onClick={() => void checkForUpdates()}>
            Check now
          </button>
        )}
      </div>
      <WhatsNewRow />
      {u.apk && (
        <div className="settings-row">
          <div>
            <div className="settings-label">App update {u.apk.versionName}</div>
            <div className="settings-hint">
              {mb(u.apk.size)}. Android asks you to confirm the install; your notes and sign-in stay.
              {u.apkState === "permission" && " Allow installs from Engram in the screen that opened; Engram restarts and carries on."}
            </div>
          </div>
          <ApkButton />
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// What's new (owner, 17 Oct: "a section I can click in Settings that shows the current version of the app, when it
// was updated, and release notes of each version"). The notes ship inside the build (src/releases.json, one entry
// per release, newest first, written for the owner), so the list works offline and never names a future version.

const SEEN_KEY = "engram.buildSeen";
const day = dateFmt({ day: "numeric", month: "short", year: "numeric" });
const dayTime = dateFmt({ day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });

/** Shell, at start: when this build first ran on this device (that's when the update reached it). */
export function rememberBuild() {
  try {
    const seen = JSON.parse(localStorage.getItem(SEEN_KEY) ?? "null") as { build: string; at: number } | null;
    if (seen?.build !== __APP_BUILD__) localStorage.setItem(SEEN_KEY, JSON.stringify({ build: __APP_BUILD__, at: Date.now() }));
  } catch {
    /* storage unavailable: the page leaves the date out */
  }
}

function buildSeenAt(): number | null {
  try {
    const seen = JSON.parse(localStorage.getItem(SEEN_KEY) ?? "null") as { build: string; at: number } | null;
    return seen?.build === __APP_BUILD__ ? seen.at : null;
  } catch {
    return null;
  }
}

/** A release's day, from its YYYY-MM-DD (noon, so no time zone moves it to another day). */
const releaseDay = (d: string) => day.format(new Date(`${d}T12:00:00`));

function WhatsNewRow() {
  const [, navigate] = useLocation();
  const since = buildSeenAt();
  const latest = RELEASES[0];
  return (
    <button className="settings-row clickable" onClick={() => navigate("/settings/version", { state: { fromSettings: true } })}>
      <div>
        <div className="settings-label">What's new</div>
        <div className="settings-hint">
          {latest ? latest.title : "Release notes"}
          {since ? ` · on this device since ${day.format(since)}` : ""}
        </div>
      </div>
      <ChevronRight size={16} className="muted" />
    </button>
  );
}

/** /settings/version: this version, when it arrived here, then every release's notes. */
export function VersionPage() {
  const u = useUpdates();
  const [, navigate] = useLocation();
  const back = () => ((history.state as { fromSettings?: boolean } | null)?.fromSettings ? history.back() : navigate("/settings", { replace: true }));
  const since = buildSeenAt();
  return (
    <div className="page settings-page">
      <div className="page-header row settings-sub-header">
        <button className="icon-btn" aria-label="Back to Settings" title="Back to Settings" onClick={back}>
          <ArrowLeft size={20} />
        </button>
        <h1>What's new</h1>
      </div>
      <div className="version-now">
        <div className="settings-label">This version</div>
        <div className="settings-hint">
          {isNative && u.info ? `App ${u.info.versionName} · screens ${__APP_BUILD__}` : __APP_BUILD__}
        </div>
        {since && <div className="settings-hint">On this device since {dayTime.format(since)}</div>}
      </div>
      <ol className="release-list">
        {RELEASES.map((r, i) => (
          <li key={`${r.date}-${r.title}`} className={i === 0 ? "current" : undefined}>
            <div className="release-head">
              <span className="release-title">{r.title}</span>
              {i === 0 && <span className="chip on release-chip">This version</span>}
              <span className="release-date muted small">{releaseDay(r.date)}</span>
            </div>
            <ul>
              {r.notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
    </div>
  );
}

function ApkButton({ small = true }: { small?: boolean }) {
  const state = useUpdates((s) => s.apkState);
  const label = state === "downloading" ? "Downloading…" : state === "installing" ? "Opening installer…" : "Update app";
  return (
    <button className={`btn primary${small ? " small" : ""}`} disabled={state === "downloading" || state === "installing"} onClick={() => void installAppUpdate()}>
      <Download size={14} /> {label}
    </button>
  );
}

/** Bottom banner: an app update to install, or (with "Ask before updating") a new version waiting for a restart. */
export function UpdateBanner() {
  const u = useUpdates();
  const ask = usePrefs((s) => s.askBeforeUpdating);
  const dismissedApk = usePrefs((s) => s.dismissedApk);
  const [laterRestart, setLaterRestart] = useState(false);
  const move = useMove();
  const [laterMove, setLaterMove] = useState(false);
  // On the old workers.dev address (moved.ts): it moves by itself when nobody's using it; otherwise, ask.
  if (move.offer && !laterMove) {
    return (
      <div className="update-banner" role="status">
        <span>{move.error ?? "Engram has moved to engrams.pages.dev."}</span>
        <button className="btn primary small" disabled={move.moving} onClick={() => void moveNow()}>
          {move.moving ? "Moving…" : "Go there"}
        </button>
        <button className="icon-btn" aria-label="Not now" onClick={() => setLaterMove(true)}>
          <X size={16} />
        </button>
      </div>
    );
  }
  if (u.apk && u.apk.versionCode !== dismissedApk) {
    return (
      <div className="update-banner" role="status">
        <span>{u.apkState === "permission" ? "Allow installs from Engram; the update carries on after." : "An app update is ready to install."}</span>
        <ApkButton />
        <button className="icon-btn" aria-label="Not now" onClick={() => usePrefs.getState().set({ dismissedApk: u.apk!.versionCode })}>
          <X size={16} />
        </button>
      </div>
    );
  }
  if (u.bundle === "ready" && ask && !laterRestart) {
    return (
      <div className="update-banner" role="status">
        <span>{u.error ?? "A new version of Engram is ready."}</span>
        <button className="btn primary small" onClick={() => void restartToUpdate()}>
          Restart
        </button>
        <button className="icon-btn" aria-label="Not now" onClick={() => setLaterRestart(true)}>
          <X size={16} />
        </button>
      </div>
    );
  }
  return null;
}
